import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnnouncements } from '@/api';
import type { AnnouncementItem } from '@/api';

export default function Announcements() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getAnnouncements(1, 50)
      .then((res) => {
        if (mounted) setAnnouncements(res.list || []);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = search.trim()
    ? announcements.filter(
        (item) =>
          item.title.toLowerCase().includes(search.toLowerCase()) ||
          item.content.toLowerCase().includes(search.toLowerCase())
      )
    : announcements;

  return (
    <div className="mobile-container bg-white min-h-screen">
      {/* Header */}
      <header className="flex items-center px-4 h-12 border-b border-background-200/50">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center text-foreground-800 cursor-pointer active:scale-95 transition-transform"
          aria-label="返回"
        >
          <i className="ri-arrow-left-line text-xl" />
        </button>
        <h1 className="flex-1 text-center text-foreground-950 text-base font-bold">
          平台公告
        </h1>
        <div className="w-8" />
      </header>

      {/* Search bar */}
      <div className="px-4 py-3">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索公告"
            className="w-full h-10 pl-9 pr-4 rounded-lg bg-background-100 border border-background-200/60 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 transition-all"
          />
        </div>
      </div>

      {/* Announcement list */}
      <main className="px-4 pb-10">
        <div className="space-y-3">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
              <p className="text-foreground-400 text-sm">加载中...</p>
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-16">
              <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3 block" />
              <p className="text-foreground-400 text-sm">{error}</p>
            </div>
          )}
          {!loading && !error && filtered.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-background-200/60 rounded-xl p-4 cursor-pointer active:scale-[0.99] transition-transform"
              onClick={() => navigate(`/announcements/${item.id}`)}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 text-[10px] font-bold bg-primary-500 text-white rounded">
                  通知
                </span>
                <span className="text-foreground-900 font-bold text-sm">
                  {item.title}
                </span>
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed mb-2">
                {item.content}
              </p>
              <p className="text-foreground-400 text-xs">
                {item.fullDate}
              </p>
            </div>
          ))}
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-inbox-2-line text-foreground-300 text-4xl mb-3 block" />
              <p className="text-foreground-400 text-sm">未找到相关公告</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}